import { supabase } from './supabase';

// ─── Customer support ticket CRUD ──────────────────────────────────────────
// Tickets are threaded conversations between a user, the AI support agent,
// and (when escalated) a human admin. The AI takes the first pass at every
// new ticket — if it's confident, the response is sent immediately; if not,
// the ticket is escalated for a human to review.

const CATEGORIES = ['general', 'billing', 'bug', 'account', 'feature', 'other'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

// ── Reads ────────────────────────────────────────────────────────────────────

export async function getMyTickets(userId) {
  if (!supabase || !userId) return [];
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('user_id', userId)
    .order('last_message_at', { ascending: false });
  if (error) { console.error('getMyTickets error:', error); return []; }
  return data || [];
}

export async function getTicket(ticketId) {
  if (!supabase || !ticketId) return null;
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('id', ticketId)
    .single();
  if (error) { console.error('getTicket error:', error); return null; }
  return data;
}

export async function getTicketMessages(ticketId) {
  if (!supabase || !ticketId) return [];
  const { data, error } = await supabase
    .from('support_messages')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) { console.error('getTicketMessages error:', error); return []; }
  return data || [];
}

// Admin-only: returns every ticket in the system. RLS enforces the is_admin
// check — non-admins get an empty list back.
export async function getAllTickets({ status } = {}) {
  if (!supabase) return [];
  let q = supabase
    .from('support_tickets')
    .select('*')
    .order('last_message_at', { ascending: false })
    .limit(200);
  if (status && status !== 'all') q = q.eq('status', status);
  const { data, error } = await q;
  if (error) { console.error('getAllTickets error:', error); return []; }
  return data || [];
}

// ── Writes ───────────────────────────────────────────────────────────────────

export async function createTicket(userId, { subject, body, category = 'general', priority = 'normal' }) {
  if (!supabase || !userId) throw new Error('Not authenticated');
  if (!subject?.trim() || !body?.trim()) throw new Error('Subject and message are required');

  const cat = CATEGORIES.includes(category) ? category : 'general';
  const pri = PRIORITIES.includes(priority) ? priority : 'normal';

  const { data: ticket, error: tErr } = await supabase
    .from('support_tickets')
    .insert({ user_id: userId, subject: subject.trim(), category: cat, priority: pri, status: 'open' })
    .select()
    .single();
  if (tErr) { console.error('createTicket error:', tErr); throw tErr; }

  // Seed the thread with the user's opening message.
  const { error: mErr } = await supabase
    .from('support_messages')
    .insert({ ticket_id: ticket.id, sender_type: 'user', sender_id: userId, body: body.trim() });
  if (mErr) { console.error('createTicket seed message error:', mErr); throw mErr; }

  return ticket;
}

export async function postUserReply(userId, ticketId, body) {
  if (!supabase || !userId || !ticketId) throw new Error('Missing ids');
  if (!body?.trim()) throw new Error('Message is required');

  const { error } = await supabase
    .from('support_messages')
    .insert({ ticket_id: ticketId, sender_type: 'user', sender_id: userId, body: body.trim() });
  if (error) { console.error('postUserReply error:', error); throw error; }

  // A new user reply re-opens the ticket so it lands back in the admin queue
  // if the AI/admin thought it was done.
  await supabase
    .from('support_tickets')
    .update({ status: 'open', resolved_at: null })
    .eq('id', ticketId)
    .eq('user_id', userId);
}

// Called from the AI responder serverless function (via the user's session)
// after Claude drafts a reply. Stored as an 'ai' message so the thread UI can
// style it distinctly from human admin replies.
export async function postAiReply(ticketId, body, { confidence = null, escalate = false } = {}) {
  if (!supabase || !ticketId) throw new Error('Missing ticketId');
  if (!body?.trim()) throw new Error('Body required');

  const { error: mErr } = await supabase
    .from('support_messages')
    .insert({ ticket_id: ticketId, sender_type: 'ai', body: body.trim() });
  if (mErr) { console.error('postAiReply error:', mErr); throw mErr; }

  const patch = {
    ai_handled: true,
    ai_confidence: confidence,
    escalated: !!escalate,
    status: escalate ? 'escalated' : 'ai_answered',
  };
  const { error: tErr } = await supabase
    .from('support_tickets')
    .update(patch)
    .eq('id', ticketId);
  if (tErr) { console.error('postAiReply status update error:', tErr); throw tErr; }
}

// Admin reply — posts a human message. Requires the caller to be an admin
// (RLS enforces it; non-admins will get a permissions error).
export async function postAdminReply(adminUserId, ticketId, body, { markResolved = false } = {}) {
  if (!supabase || !adminUserId || !ticketId) throw new Error('Missing ids');
  if (!body?.trim()) throw new Error('Body required');

  const { error: mErr } = await supabase
    .from('support_messages')
    .insert({ ticket_id: ticketId, sender_type: 'admin', sender_id: adminUserId, body: body.trim() });
  if (mErr) { console.error('postAdminReply error:', mErr); throw mErr; }

  const patch = markResolved
    ? { status: 'resolved', resolved_at: new Date().toISOString() }
    : { status: 'awaiting_user' };
  const { error: tErr } = await supabase
    .from('support_tickets')
    .update(patch)
    .eq('id', ticketId);
  if (tErr) { console.error('postAdminReply status update error:', tErr); throw tErr; }
}

export async function setTicketStatus(ticketId, status) {
  if (!supabase || !ticketId) throw new Error('Missing ticketId');
  const patch = { status };
  if (status === 'resolved' || status === 'closed') patch.resolved_at = new Date().toISOString();
  if (status === 'open') patch.resolved_at = null;
  const { error } = await supabase.from('support_tickets').update(patch).eq('id', ticketId);
  if (error) { console.error('setTicketStatus error:', error); throw error; }
}

// ─── AI auto-responder ─────────────────────────────────────────────────────
// Calls the /api/support-ai-respond serverless function. The server reads the
// ticket + thread, asks Claude to draft a reply, decides whether to escalate,
// and posts the message back as the 'ai' sender. Returns { reply, confidence,
// escalate } so the caller can update UI optimistically.

export async function requestAiReply(ticket, messages, profile) {
  const res = await fetch('/api/support-ai-respond', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ticket: {
        id: ticket.id,
        subject: ticket.subject,
        category: ticket.category,
        priority: ticket.priority,
      },
      messages: (messages || []).map(m => ({
        sender_type: m.sender_type,
        body: m.body,
        created_at: m.created_at,
      })),
      profile: profile ? {
        full_name: profile.full_name,
        email: profile.email,
        plan: profile.plan,
        show_name: profile.show_name,
      } : null,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `AI responder failed (${res.status})`);
  }
  return res.json();
}

// Convenience wrapper: kicks off the AI responder for a freshly-created ticket
// and persists the resulting reply in a single call. Used by SupportPage right
// after createTicket() resolves.
export async function runAiOnNewTicket(ticket, openingMessage, profile) {
  const messages = [{ sender_type: 'user', body: openingMessage, created_at: ticket.created_at }];
  const { reply, confidence, escalate } = await requestAiReply(ticket, messages, profile);
  if (reply) {
    await postAiReply(ticket.id, reply, { confidence, escalate });
  }
  return { reply, confidence, escalate };
}

export const SUPPORT_CATEGORIES = CATEGORIES;
export const SUPPORT_PRIORITIES = PRIORITIES;
